import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  Menu,
  MenuItem,
  styled,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  TextField,
  Button,
  Chip,
  Divider,
  AppBar,
  Toolbar,
  Badge,
  InputAdornment,
  Select,
} from "@mui/material";

import LeftToolbar from "./LeftToolbar";
import Footer from "./Footer";
import NotificationSnackbar from "./NotificationBar";
import { useNavigate } from "react-router-dom";

import AddNewRBMException from "./AddNewRBMException";
// import EditRBMException from "./EditRBMException";
import ViewRBMException from "./ViewRBMException";

import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EventIcon from "@mui/icons-material/Event";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SearchIcon from "@mui/icons-material/Search";
import FilterIcon from "@mui/icons-material/FilterList";
import CalendarToday from "@mui/icons-material/CalendarToday";

// ==========================================
// STYLED COMPONENTS
// ==========================================
const MainContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f5f7fa",
});

const MainContent = styled(Box)(({ theme, $collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: $collapsed ? "80px" : "280px",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)",
  overflowX: "auto",
  background: "white",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  "&::-webkit-scrollbar": { height: "6px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
    borderRadius: "3px",
  },
  [theme.breakpoints.down("sm")]: { borderRadius: "12px" },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: alpha(theme.palette.primary.light, 0.03),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.08),
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[1],
  },
  transition: theme.transitions.create(["background-color", "transform"], {
    duration: theme.transitions.duration.shortest,
  }),
  "&:last-child td, &:last-child th": { border: 0 },
}));

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  "&:first-of-type": {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  [theme.breakpoints.down("sm")]: { padding: theme.spacing(1.5) },
}));

const StatusBadge = styled("span")(({ action, theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 12px",
  borderRadius: "16px",
  fontWeight: 600,
  fontSize: "0.75rem",
  textTransform: "capitalize",
  backgroundColor: alpha(theme.palette.info.light, 0.2),
  color: theme.palette.info.dark,
  "&:before": {
    content: '""',
    width: 6,
    height: 6,
    borderRadius: "50%",
    marginRight: 6,
    backgroundColor: theme.palette.info.main,
  },
}));

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const options = { year: "numeric", month: "short", day: "numeric" };
  return new Date(dateString).toLocaleDateString(undefined, options);
};

// ==========================================
// MAIN COMPONENT
// ==========================================
function RBMExceptions() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("RBM Exceptions");

  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [institutionFilter, setInstitutionFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [dateFilter, setDateFilter] = useState({
    mode: "none",
    singleDate: "",
    startDate: "",
    endDate: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "uiId",
    direction: "asc",
  });
  const itemsPerPage = 5;

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedException, setSelectedException] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [user, setUser] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      return userData ? JSON.parse(userData) : {};
    } catch (error) {
      return {};
    }
  });

  useEffect(() => {
    fetchExceptions();
  }, []);

  const fetchExceptions = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/support/rbm-employees-exceptions");
      if (!res.ok) {
        throw new Error(
          `Failed to fetch RBM exceptions. Status: ${res.status}`,
        );
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.recordset || [];
      const dataWithUiId = list.map((item, index) => ({
        ...item,
        uiId: index + 1,
      }));

      setExceptions(dataWithUiId);
    } catch (error) {
      console.error("Error fetching RBM exceptions:", error);
      setError("Failed to fetch exceptions. Please try again later.");
      setExceptions([]);
    } finally {
      setLoading(false);
    }
  };

  const uniqueInstitutions = [
    ...new Set(exceptions.map((item) => item.Institution).filter(Boolean)),
  ];

  const uniqueActions = [
    ...new Set(exceptions.map((item) => item.action).filter(Boolean)),
  ];

  const filteredExceptions = exceptions.filter((item) => {
    const search = searchTerm.toLowerCase().trim();
    const matchesSearch =
      !search ||
      item.id?.toString().toLowerCase().includes(search) ||
      item.employeeId?.toLowerCase().includes(search) ||
      item.employee_name?.toLowerCase().includes(search) ||
      item.Position?.toLowerCase().includes(search) ||
      item.Institution?.toLowerCase().includes(search) ||
      item.FiscalYear?.toLowerCase().includes(search) ||
      item.QuarterName?.toLowerCase().includes(search);

    const matchesInstitution =
      institutionFilter === "" || item.Institution === institutionFilter;
    const matchesAction = actionFilter === "" || item.action === actionFilter;

    let matchesDate = true;
    if (dateFilter.mode === "single" && dateFilter.singleDate) {
      const itemDate = new Date(item.startDate).toISOString().split("T")[0];
      matchesDate = itemDate === dateFilter.singleDate;
    } else if (dateFilter.mode === "range") {
      const itemDate = new Date(item.startDate).getTime();
      const startDate = dateFilter.startDate
        ? new Date(dateFilter.startDate).getTime()
        : 0;
      const endDate = dateFilter.endDate
        ? new Date(dateFilter.endDate).getTime() + 86400000
        : Infinity;
      matchesDate = itemDate >= startDate && itemDate < endDate;
    }

    return matchesSearch && matchesInstitution && matchesAction && matchesDate;
  });
  const sortedExceptions = [...filteredExceptions].sort((a, b) => {
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (sortConfig.key === "uiId") {
      return sortConfig.direction === "asc"
        ? Number(aValue) - Number(bValue)
        : Number(bValue) - Number(aValue);
    }

    const comparison = String(aValue)
      .toLowerCase()
      .localeCompare(String(bValue).toLowerCase());

    return sortConfig.direction === "asc" ? comparison : -comparison;
  });
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedExceptions.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ key, direction });
  };

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedException(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const highlightMatch = (text, term) => {
    if (!term || !text) return text;
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedTerm})`, "gi");
    return text
      .toString()
      .split(regex)
      .map((part, i) =>
        regex.test(part) ? <mark key={i}>{part}</mark> : part,
      );
  };

  return (
    <MainContainer>
      <AppBar
        position="fixed"
        sx={{
          backgroundImage: "linear-gradient(195deg, #0a2e52 0%, #1565C0 200%)",
          boxShadow:
            "0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(0, 0, 0, 0.4)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          left: collapsed ? "80px" : "280px",
          width: `calc(100% - ${collapsed ? "80px" : "280px"})`,
          transition: theme.transitions.create(["width", "left"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          [theme.breakpoints.down("sm")]: { left: 0, width: "100%" },
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
            RBM Exceptions Portal
          </Typography>
          <IconButton color="inherit">
            <Badge
              badgeContent={
                exceptions.length > 999 ? "999+" : exceptions.length
              }
              color="error"
              max={9999}
              onClick={() => {
                if (user.roleId === "ADMIN" || user.roleId === "SUPPORT")
                  navigate("/Dashboard");
              }}
              sx={{
                cursor:
                  user.roleId === "ADMIN" || user.roleId === "SUPPORT"
                    ? "pointer"
                    : "not-allowed",
              }}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <LeftToolbar
        selectedMenu={selectedMenu}
        onSelectMenu={setSelectedMenu}
        onCollapse={setCollapsed}
      />

      <MainContent $collapsed={collapsed}>
        <Toolbar />

        <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {institutionFilter && (
            <Chip
              label={`Institution: ${institutionFilter}`}
              onDelete={() => setInstitutionFilter("")}
              sx={{ borderRadius: "12px" }}
            />
          )}
          {actionFilter && (
            <Chip
              label={`Action: ${actionFilter}`}
              onDelete={() => setActionFilter("")}
              sx={{ borderRadius: "12px" }}
            />
          )}
          {dateFilter.mode === "single" && dateFilter.singleDate && (
            <Chip
              label={`Start Date: ${new Date(dateFilter.singleDate).toLocaleDateString()}`}
              onDelete={() => setDateFilter({ ...dateFilter, singleDate: "" })}
              sx={{ borderRadius: "12px" }}
            />
          )}
          {dateFilter.mode === "range" &&
            (dateFilter.startDate || dateFilter.endDate) && (
              <Chip
                label={`Range: ${dateFilter.startDate ? new Date(dateFilter.startDate).toLocaleDateString() : "..."} to ${dateFilter.endDate ? new Date(dateFilter.endDate).toLocaleDateString() : "..."}`}
                onDelete={() =>
                  setDateFilter({ mode: "range", startDate: "", endDate: "" })
                }
                sx={{ borderRadius: "12px" }}
              />
            )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            gap: 2,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <TextField
            label="Search exceptions..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: isMobile ? "100%" : 400,
              "& .MuiOutlinedInput-root": { borderRadius: "12px" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{ display: "flex", gap: 2, width: isMobile ? "100%" : "auto" }}
          >
            <Select
              value={dateFilter.mode}
              onChange={(e) =>
                setDateFilter({ ...dateFilter, mode: e.target.value })
              }
              size="small"
              sx={{ minWidth: 140, borderRadius: "12px" }}
            >
              <MenuItem value="none">No date filter</MenuItem>
              <MenuItem value="single">Specific Start Date</MenuItem>
              <MenuItem value="range">Date range</MenuItem>
            </Select>

            {dateFilter.mode === "single" && (
              <TextField
                type="date"
                size="small"
                value={dateFilter.singleDate}
                onChange={(e) =>
                  setDateFilter({ ...dateFilter, singleDate: e.target.value })
                }
                sx={{ borderRadius: "12px" }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {dateFilter.mode === "range" && (
              <>
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={dateFilter.startDate}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, startDate: e.target.value })
                  }
                  sx={{ borderRadius: "12px" }}
                  InputLabelProps={{ shrink: true }}
                />
                <Typography sx={{ alignSelf: "center" }}>to</Typography>
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={dateFilter.endDate}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, endDate: e.target.value })
                  }
                  sx={{ borderRadius: "12px" }}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}

            {(dateFilter.mode === "single" || dateFilter.mode === "range") && (
              <Button
                variant="outlined"
                onClick={() =>
                  setDateFilter({
                    mode: "none",
                    singleDate: "",
                    startDate: "",
                    endDate: "",
                  })
                }
                sx={{ borderRadius: "12px" }}
              >
                Clear
              </Button>
            )}

            <Select
              value={institutionFilter}
              onChange={(e) => setInstitutionFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                minWidth: 150,
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": { borderRadius: "12px" },
              }}
              startAdornment={
                <InputAdornment position="start">
                  <FilterIcon fontSize="small" />
                </InputAdornment>
              }
            >
              <MenuItem value="">All Institutions</MenuItem>
              {uniqueInstitutions.map((inst) => (
                <MenuItem key={inst} value={inst}>
                  {inst}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                minWidth: 130,
                borderRadius: "12px",
                "& .MuiOutlinedInput-root": { borderRadius: "12px" },
              }}
            >
              <MenuItem value="">All Actions</MenuItem>
              {uniqueActions.map((action) => (
                <MenuItem key={action} value={action}>
                  {action}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            p: 3,
            mb: 3,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Exception List
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setAddModalOpen(true)}
              startIcon={<AddIcon />}
              sx={{
                fontWeight: 600,
                borderRadius: "999px",
                textTransform: "none",
                bgcolor: theme.palette.primary.main,
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                  bgcolor: theme.palette.primary.dark,
                },
              }}
            >
              Add Exception
            </Button>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {filteredExceptions.length}{" "}
              {filteredExceptions.length === 1 ? "record" : "records"} found
            </Typography>
          </Box>

          <ResponsiveTableContainer component={Paper}>
            <Table size={isMobile ? "small" : "medium"} sx={{ minWidth: 1050 }}>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: theme.palette.primary.light,
                    "& th": {
                      color: "white",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  {[
                    { id: "uiId", label: "ID" },
                    { id: "employee_name", label: "Employee Name" },
                    { id: "Position", label: "Position" },
                    { id: "Institution", label: "Institution" },
                    { id: "Period", label: "Period" },
                    { id: "action", label: "Action" },
                    { id: "startDate", label: "Duration" },
                    { id: "actions", label: "Options" },
                  ].map((column) => (
                    <ModernTableCell
                      key={column.id}
                      onClick={() => {
                        if (column.id !== "actions") handleSort(column.id);
                      }}
                      sx={{
                        cursor: column.id !== "actions" ? "pointer" : "default",
                        "&:hover": {
                          backgroundColor:
                            column.id !== "actions"
                              ? alpha(theme.palette.primary.main, 0.2)
                              : "inherit",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        {column.label}
                        {sortConfig.key === column.id &&
                          column.id !== "actions" && (
                            <Box ml={1} display="flex" flexDirection="column">
                              <span
                                style={{
                                  lineHeight: 0.5,
                                  color:
                                    sortConfig.direction === "asc"
                                      ? "#fff"
                                      : alpha("#fff", 0.5),
                                  fontSize: "0.7rem",
                                }}
                              >
                                ▲
                              </span>
                              <span
                                style={{
                                  lineHeight: 0.5,
                                  color:
                                    sortConfig.direction === "desc"
                                      ? "#fff"
                                      : alpha("#fff", 0.5),
                                  fontSize: "0.7rem",
                                }}
                              >
                                ▼
                              </span>
                            </Box>
                          )}
                      </Box>
                    </ModernTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <ModernTableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Loading exceptions...
                      </Typography>
                    </ModernTableCell>
                  </TableRow>
                ) : currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <StyledTableRow key={item.uiId} hover>
                      <ModernTableCell>
                        <span style={{ fontWeight: 600 }}>#{item.uiId}</span>
                      </ModernTableCell>
                      <ModernTableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {highlightMatch(item.employee_name, searchTerm) ||
                              "—"}
                          </Typography>
                          {item.employeeId && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ID: {highlightMatch(item.employeeId, searchTerm)}
                            </Typography>
                          )}
                        </Box>
                      </ModernTableCell>
                      <ModernTableCell>
                        {highlightMatch(item.Position, searchTerm) || "—"}
                      </ModernTableCell>
                      <ModernTableCell>
                        {highlightMatch(item.Institution, searchTerm) || "—"}
                      </ModernTableCell>

                      {/* NEW Period Cell (Fiscal Year & Quarter) */}
                      <ModernTableCell>
                        <Box display="flex" flexDirection="column">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.primary.dark,
                            }}
                          >
                            {highlightMatch(item.FiscalYear, searchTerm) || "—"}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {highlightMatch(item.QuarterName, searchTerm) ||
                              "—"}
                          </Typography>
                        </Box>
                      </ModernTableCell>
                      <ModernTableCell>
                        <StatusBadge action={item.action}>
                          {item.action || "—"}
                        </StatusBadge>
                        {item.gracePeriodDays && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            Grace: {item.gracePeriodDays} days
                          </Typography>
                        )}
                      </ModernTableCell>
                      <ModernTableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={0.8}
                            color="text.secondary"
                          >
                            <EventIcon sx={{ fontSize: 14 }} />
                            <Typography variant="caption">
                              {formatDate(item.startDate)} -{" "}
                              {formatDate(item.endDate)}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Opened by: {item.OpenedBy || "—"}
                          </Typography>
                        </Box>
                      </ModernTableCell>
                      <ModernTableCell>
                        <Tooltip title="Options" arrow>
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, item)}
                            size="small"
                            sx={{
                              color: theme.palette.primary.main,
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.1,
                                ),
                              },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ModernTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <TableRow>
                    <ModernTableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: "text.secondary" }}
                      >
                        No records found
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Try adjusting your search or filters.
                      </Typography>
                    </ModernTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ResponsiveTableContainer>

          {filteredExceptions.length > itemsPerPage && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={Math.ceil(filteredExceptions.length / itemsPerPage)}
                page={currentPage}
                onChange={(e, value) => setCurrentPage(value)}
                color="primary"
                size={isMobile ? "small" : "medium"}
              />
            </Box>
          )}
        </Box>
      </MainContent>

      <Footer collapsed={collapsed} />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 160,
            borderRadius: "12px",
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setEditModalOpen(true);
            handleMenuClose();
          }}
          sx={{
            gap: 1.5,
            py: 1.2,
            px: 2,
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
          }}
        >
          <EditIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight={500}>
            Edit
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setViewModalOpen(true);
            handleMenuClose();
          }}
          sx={{
            gap: 1.5,
            py: 1.2,
            px: 2,
            "&:hover": {
              backgroundColor: alpha(theme.palette.info.main, 0.06),
            },
          }}
        >
          <VisibilityIcon fontSize="small" color="info" />
          <Typography variant="body2" fontWeight={500}>
            View Details
          </Typography>
        </MenuItem>
      </Menu>

      {/* <EditRBMException
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedException(null);
        }}
        onSaveSuccess={fetchExceptions}
        exception={selectedException}
      /> */}
      <ViewRBMException
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        exception={selectedException}
      />
      <AddNewRBMException
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => {
          setAddModalOpen(false);
          fetchExceptions();
        }}
      />

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </MainContainer>
  );
}

export default RBMExceptions;
